import { useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet } from 'react-router';
import { Dayjs } from 'dayjs';

import Sidebar from '../../components/NWL/Sidebar/Sidebar';
import LocationService from '@/domain/Location/LocationService';
import { DashboardContext, NWLContext } from '../../contexts';
import { getDigitalTwinsSimulations, getDigitalTwins } from '../../services/digitalTwins';
import { lockFirstSuccessCompletedSimTime } from '@/components/utils/simSuccessTime';

import './nwldashboardlayout.css';

const locationService = new LocationService();

/**
 * -----------------------------
 * Helpers
 * -----------------------------
 */
function currentStatus(payload: any) {
	const status = payload?.data?.[0]?.summary?.status;
	return status === 'COMPLETED' || status === 'IN_PROGRESS' || status === 'FAILED' ? status : null;
}

function latestCompletedBucket(payload: any) {
	const arr = Array.isArray(payload?.data) ? payload.data : [];
	const now = arr[0];
	if (now?.summary?.status === 'COMPLETED') return now;
	return arr.slice(1).find((b: any) => b?.summary?.status === 'COMPLETED') || null;
}

function pickActiveBucket(result: any) {
	const buckets = Array.isArray(result?.data) ? result.data : [];
	const status = currentStatus(result);
	if (status === 'IN_PROGRESS') {
		return buckets[1] || latestCompletedBucket(result) || buckets[0] || null;
	}
	return buckets[0] || latestCompletedBucket(result) || null;
}

const getObjFuncFromSims = (sims: Array<{ value: number; strategy: string }>) =>
	sims.map(({ value, strategy }, i) => ({
		score: Number(value),
		name: String(strategy || '').toLowerCase(),
		index: i,
	}));

const recommendStrategy = (strategies: { score: number; name: string; index: number }[]) => {
	if (!strategies?.length) return undefined;
	const minScore = Math.min(...strategies.map((s) => s.score));
	const tied = strategies.filter((s) => s.score === minScore);
	const priority = ['default', 'small storm', 'large storm'];
	const preferred = priority.find((p) => tied.some((s) => s.name === p)) || tied[0]?.name;
	return strategies.find((s) => s.name === preferred);
};

type SimStatus = 'COMPLETED' | 'IN_PROGRESS' | 'FAILED' | null;

type LocationRow = {
	locationId: number;
	locationName: string;
	defaultSelected?: boolean;
};

type DecisionEnginePerLocation = {
	locationId: number;
	digitalTwinId: number | null;

	data: any;

	simStatus: SimStatus;
	recommendedStrategy: { score: number; name: string; index: number } | null;
	recommendedStrategyName: string | null;
	lastSuccessfulRefreshTime?: Date;

	strategyIndexes: {
		default: number | null;
		recommended: number | null;
	};
};

function summariseDecisionEngineResult(result: any) {
	const latestStatus = currentStatus(result);

	const completed = latestCompletedBucket(result);
	const lastCompletedRec = 
		completed?.summary?.recommendedStrategy
			? String(completed.summary.recommendedStrategy).toLowerCase()
			: null;

	const updatedDate = lockFirstSuccessCompletedSimTime(result) ?? undefined;

	const activeBucket = pickActiveBucket(result);
	const effectiveStatus = activeBucket?.summary?.status ?? latestStatus;
	const sims = Array.isArray(activeBucket?.simulations) ? activeBucket.simulations : [];

	if (!sims.length) {
		return {
			simStatus: effectiveStatus,
			recommendedStrategy: null,
			recommendedStrategyName: lastCompletedRec,
			strategyIndexes: { default: 0, recommended: 0 },
			lastSuccessfulRefreshTime: updatedDate,
		};
	}

	const scores = getObjFuncFromSims(
		sims.map((s: any) => ({
			value: Number(s?.value ?? 0),
			strategy: String(s?.strategy || ''),
		})),
	);

	const defIdx = scores.find(({ name }) => name === 'default')?.index ?? 0;
	const decision = recommendStrategy(scores) || null;

	const fromBucket = String(activeBucket?.summary?.recommendedStrategy || '').toLowerCase() || null;

	const effectiveName =
		effectiveStatus === 'COMPLETED'
			? fromBucket || decision?.name || lastCompletedRec || null
			: lastCompletedRec || decision?.name || null;

	return {
		simStatus: effectiveStatus,
		recommendedStrategy: decision,
		recommendedStrategyName: effectiveName,
		strategyIndexes: {
			default: defIdx,
			recommended: decision?.index ?? 0,
		},
		lastSuccessfulRefreshTime: updatedDate,
	};
}

/**
 * -----------------------------
 * Layout Component
 * -----------------------------
 */
const NwlDashboardLayout = () => {
	const [context, setContext] = useState<any>(DashboardContext);
	const [expanded, setExpanded] = useState(true);

	const [locations, setLocations] = useState<LocationRow[]>([]);
	const [organizationId, setOrganizationId] = useState<number | null>(null);

	const [decisionEngineByLocation, setDecisionEngineByLocation] = useState<
		Record<number, DecisionEnginePerLocation>
	>({});

	const [selectedAssetItem, setSelectedAssetItem] = useState(null);
	const [showComparisonGraph, setShowComparisonGraph] = useState(false);
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | undefined>(undefined);

	/**
	 * Load locations + org id
	 */
	useEffect(() => {
		const userId = localStorage.getItem('userId') || '';
		locationService.getLocationsByUser(userId).then((value: any) => {
			const locationRows: LocationRow[] = Array.isArray(value)
				? value.map((loc: any) => ({
						locationId: loc.locationId || loc.id,
						locationName: loc.locationName || loc.name,
						defaultSelected: loc.defaultSelected,
					}))
				: [];
			localStorage.setItem('locations', JSON.stringify(locationRows));
			window.dispatchEvent(new Event('nwl:locations-updated'));
			setLocations(locationRows || []);
		});

		const security = JSON.parse(localStorage.getItem('security') || '{}');
		setOrganizationId(security?.organizationId ?? null);
	}, []);

	/**
	 * Set default selected location
	 */
	useEffect(() => {
		if (!locations.length) return;

		const def = locations.find((l) => l.defaultSelected) ?? locations[0];
		if (!def) return;

		setContext((prev: any) => ({ ...prev, selectedLocation: def.locationId }));
	}, [locations]);

	/**
	 * Fetch decision engine data for ONE location
	 */
	const fetchDecisionEngineForLocation = useCallback(
		async (loc: LocationRow): Promise<DecisionEnginePerLocation | null> => {
			if (!organizationId) return null;

			const dt = await getDigitalTwins(organizationId, loc.locationId);
			const digitalTwinId = dt?.id ?? null;

			// If NO digitalTwinId => NO recommendation (null everything)
			if (!digitalTwinId) {
				return {
					locationId: loc.locationId,
					digitalTwinId: null,
					data: null,
					simStatus: null,
					recommendedStrategy: null,
					recommendedStrategyName: null,
					lastSuccessfulRefreshTime: undefined,
					strategyIndexes: { default: null, recommended: null },
				};
			}

			let result: any = null;
			try {
				result = await getDigitalTwinsSimulations(organizationId, loc.locationId, digitalTwinId);
			} catch {
				result = null;
			}

			if (!result) {
				// digitalTwin exists but sims fetch failed -> mark FAILED
				return {
					locationId: loc.locationId,
					digitalTwinId,
					data: null,
					simStatus: 'FAILED',
					recommendedStrategy: null,
					recommendedStrategyName: null,
					lastSuccessfulRefreshTime: undefined,
					strategyIndexes: { default: null, recommended: null },
				};
			}

			const summary = summariseDecisionEngineResult(result);

			return {
				locationId: loc.locationId,
				digitalTwinId,
				data: result,
				simStatus: summary.simStatus,
				recommendedStrategy: summary.recommendedStrategy,
				recommendedStrategyName: summary.recommendedStrategyName,
				lastSuccessfulRefreshTime: summary.lastSuccessfulRefreshTime,
				strategyIndexes: summary.strategyIndexes,
			};
		},
		[organizationId],
	);

	/**
	 * Fetch decision engine for ALL locations
	 */
	const fetchDecisionEngineForAllLocations = useCallback(async () => {
		if (!organizationId || !locations.length) return;

		const settled = await Promise.allSettled(
			locations.map(async (loc) => fetchDecisionEngineForLocation(loc)),
		);

		const next: Record<number, DecisionEnginePerLocation> = {};

		for (const s of settled) {
			if (s.status === 'fulfilled' && s.value) {
				next[s.value.locationId] = s.value;
			}
		}

		setDecisionEngineByLocation(next);
	}, [organizationId, locations, fetchDecisionEngineForLocation]);

	useEffect(() => {
		fetchDecisionEngineForAllLocations();
	}, [fetchDecisionEngineForAllLocations]);

	/**
	 * If there is NO digitalTwinId for that location, we DO NOT compute/update recommendation.
	 */
	useEffect(() => {
		const onUpdated = async (e: any) => {
			const payload = e?.detail?.payload;
			const locIdFromEvent: number | undefined = e?.detail?.locationId;

			// If we can’t tell which location changed, refresh all.
			if (!locIdFromEvent) {
				fetchDecisionEngineForAllLocations();
				return;
			}

			// If the event explicitly includes digitalTwinId, use it.
			const eventDigitalTwinId: number | null | undefined = e?.detail?.digitalTwinId ?? undefined;

			setDecisionEngineByLocation((prev) => {
				const prevEntry = prev[locIdFromEvent];

				// Determine if this location has a twin (prefer eventDigitalTwinId, else prevEntry)
				const twinId = eventDigitalTwinId ?? prevEntry?.digitalTwinId ?? null;

				// If no twinId, null everything for that location
				if (!twinId) {
					return {
						...prev,
						[locIdFromEvent]: {
							locationId: locIdFromEvent,
							digitalTwinId: null,
							data: null,
							simStatus: null,
							recommendedStrategy: null,
							recommendedStrategyName: null,
							lastSuccessfulRefreshTime: undefined,
							strategyIndexes: { default: null, recommended: null },
						},
					};
				}

				// If we got a payload shaped like simulations, update from payload without refetch
				if (payload && Array.isArray(payload?.data)) {
					const summary = summariseDecisionEngineResult(payload);

					return {
						...prev,
						[locIdFromEvent]: {
							locationId: locIdFromEvent,
							digitalTwinId: twinId,
							data: payload,
							simStatus: summary.simStatus,
							recommendedStrategy: summary.recommendedStrategy,
							recommendedStrategyName: summary.recommendedStrategyName,
							lastSuccessfulRefreshTime: summary.lastSuccessfulRefreshTime,
							strategyIndexes: summary.strategyIndexes,
						},
					};
				}

				// Otherwise keep current state; we’ll refetch below (outside setState)
				return prev;
			});

			// If payload wasn’t usable, refetch just that location
			if (!payload || !Array.isArray(payload?.data)) {
				const loc = locations.find((l) => l.locationId === locIdFromEvent);
				if (!loc) return;

				const next = await fetchDecisionEngineForLocation(loc);
				if (!next) return;

				setDecisionEngineByLocation((prev) => ({ ...prev, [loc.locationId]: next }));
			}
		};

		window.addEventListener('nwl:simulation-updated', onUpdated);
		return () => window.removeEventListener('nwl:simulation-updated', onUpdated);
	}, [locations, fetchDecisionEngineForLocation, fetchDecisionEngineForAllLocations]);

	/**
	 * Derive ACTIVE location decision engine
	 */
	const activeDecisionEngine: DecisionEnginePerLocation | null = useMemo(() => {
		const activeLocId = context?.selectedLocation;
		if (!activeLocId) return null;
		return decisionEngineByLocation[activeLocId] ?? null;
	}, [context?.selectedLocation, decisionEngineByLocation]);

	/**
	 * Context value
	 */
	const decisionEngineContextValues = useMemo(() => {
		return {
			decisionEngineByLocation,

			data: activeDecisionEngine?.data ?? null,
			strategyIndexes: activeDecisionEngine?.strategyIndexes ?? {
				default: null,
				recommended: null,
			},
			digitalTwinId: activeDecisionEngine?.digitalTwinId ?? null,
			recommendedStrategy: activeDecisionEngine?.recommendedStrategy ?? null,
			recommendedStrategyName: activeDecisionEngine?.recommendedStrategyName ?? null,
			simStatus: activeDecisionEngine?.simStatus ?? null,
			lastSuccessfulRefreshTime: activeDecisionEngine?.lastSuccessfulRefreshTime,

			selectedAssetItem,
			sidebarExpanded: expanded,
			showComparisonGraph,
			dateRange,

			setSidebarExpanded: setExpanded,
			setSelectedAssetItem,
			setShowComparisonGraph,
			setDateRange,
		};
	}, [
		decisionEngineByLocation,
		activeDecisionEngine,
		selectedAssetItem,
		expanded,
		showComparisonGraph,
		dateRange,
	]);

	return (
		<DashboardContext.Provider value={{ value: context, setValue: setContext }}>
			<NWLContext.Provider value={{ decisionEngine: decisionEngineContextValues }}>
				<div className="nwl-dashboard-layout">
					<div className="flex">
						<Sidebar />
					</div>
					<main>
						<Outlet />
					</main>
				</div>
			</NWLContext.Provider>
		</DashboardContext.Provider>
	);
};

export default NwlDashboardLayout;
